import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader2, ShieldCheck, TriangleAlert } from 'lucide-react'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { ApiError } from '../utils/api'
import { hmrcAuthService } from '../utils/hmrcAuthService'
import { clearHmrcTokens } from '../utils/hmrcTokenService'

type CallbackState = 'idle' | 'processing' | 'success' | 'error'

const parseQueryParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search)
  const entries: Record<string, string> = {}
  params.forEach((value, key) => {
    entries[key] = value
  })
  return entries
}

export default function HmrcAuthCallback() {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<CallbackState>('idle')
  const [message, setMessage] = useState<string>('Validating HMRC authorization...')
  const [details, setDetails] = useState<string | null>(null)
  
  // Use ref to track if callback has been processed to prevent duplicate calls
  const processedRef = useRef<string | null>(null)
  const isProcessingRef = useRef(false)

  const queryParams = useMemo(
    () => parseQueryParams(location.search),
    [location.search]
  )

  useEffect(() => {
    const exchangeCode = async () => {
      // Prevent duplicate processing
      if (status !== 'idle' || isProcessingRef.current) return

      const { code, state, error, error_description } = queryParams

      // Create a unique key for this callback to prevent duplicate processing
      const callbackKey = code ? `hmrc_callback_${code}` : null

      // Check if we've already processed this callback
      if (callbackKey && processedRef.current === callbackKey) {
        console.log('Callback already processed, skipping...')
        return
      }

      // Check if already processed in sessionStorage (survives page reloads)
      if (callbackKey && sessionStorage.getItem(callbackKey)) {
        console.log('Callback already processed (from sessionStorage), redirecting...')
        const redirectPath = sessionStorage.getItem('hmrcAuthRedirect') || '/dashboard'
        sessionStorage.removeItem('hmrcAuthRedirect')
        navigate(redirectPath, { replace: true })
        return
      }

      if (error || error_description) {
        setStatus('error')
        setMessage('HMRC authorization was cancelled')
        setDetails(
          error_description ||
            error ||
            'You can close this window and try authorizing again.'
        )
        return
      }

      if (!code || !state) {
        setStatus('error')
        setMessage('Missing HMRC authorization parameters')
        setDetails('The HMRC callback did not include the required code/state values.')
        return
      }

      // Mark as processing
      isProcessingRef.current = true
      setStatus('processing')
      setMessage('Completing HMRC authorization...')
      setDetails(null)

      try {
        const { response } = await hmrcAuthService.handleCallback(queryParams)
        
        // Extract response data - response structure: { success, data, message }
        const responseData = response as any
        const success =
          typeof responseData?.success === 'boolean'
            ? responseData.success
            : true

        if (!success) {
          setStatus('error')
          setMessage('HMRC authorization failed')
          setDetails(responseData?.message || 'Authorization failed')
          isProcessingRef.current = false
          return
        }

        // Ensure no HMRC tokens are stored locally.
        clearHmrcTokens()

        // Mark this callback as processed
        if (callbackKey) {
          processedRef.current = callbackKey
          sessionStorage.setItem(callbackKey, 'processed')
        }

        setStatus('success')
        setMessage('HMRC authorization completed successfully.')
        
        // Check if there's a stored redirect path from filing selection
        const redirectPath = sessionStorage.getItem('hmrcAuthRedirect') || '/dashboard'
        sessionStorage.removeItem('hmrcAuthRedirect')
        
        setTimeout(() => {
          navigate(redirectPath, { replace: true })
        }, 2000)
      } catch (err) {
        console.error('HMRC callback exchange failed:', err)
        isProcessingRef.current = false
        if (err instanceof ApiError) {
          setStatus('error')
          setMessage('HMRC authorization failed')
          setDetails(err.message || 'Unable to verify authorization with HMRC.')
        } else if (err instanceof Error) {
          setStatus('error')
          setMessage('HMRC authorization failed')
          setDetails(err.message)
        } else {
          setStatus('error')
          setMessage('HMRC authorization failed')
          setDetails('An unexpected error occurred. Please try again.')
        }
      }
    }

    void exchangeCode()
  }, [navigate, queryParams, status])

  return (
    <AuthenticatedLayout activeTab='dashboard'>
      <div className='flex min-h-[400px] items-center justify-center rounded-[15px] bg-white'>
        <div className='max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100'>
            {status === 'processing' && (
              <Loader2 className='h-8 w-8 animate-spin text-gray-500' />
            )}
            {status === 'success' && (
              <ShieldCheck className='h-8 w-8 text-green-600' />
            )}
            {status === 'error' && (
              <TriangleAlert className='h-8 w-8 text-red-600' />
            )}
          </div>
          <h1 className='mb-2 text-xl font-semibold text-gray-900'>{message}</h1>
          {details && <p className='text-sm text-gray-600'>{details}</p>}
          {status === 'success' && (
            <p className='mt-4 text-sm text-gray-500'>
              Redirecting you back to the dashboard…
            </p>
          )}
          {status === 'error' && (
            <div className='mt-6 flex flex-col gap-3'>
              <button
                onClick={() => navigate('/dashboard')}
                className='rounded-full bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-gray-800'
              >
                Return to dashboard
              </button>
              <button
                onClick={() => navigate(-1)}
                className='rounded-full border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100'
              >
                Go back
              </button>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}
